import { useEffect } from "react";
import { useTourStoreApi } from "@heroncendre/player";

/**
 * ============================================================================
 *  useOscillationCamera.ts — La vue qui suit la main de l'indicateur
 * ----------------------------------------------------------------------------
 *  C'est la démonstration cause / effet du handoff : la main glisse, la vue
 *  tourne dans le même sens, à la même période. Le visiteur voit que le geste
 *  fait tourner le panorama avant même de l'avoir essayé.
 *
 *  Le cap s'écrit dans le store, comme le fait la rotation de l'accueil — et en
 *  ÉCART, pas en valeur absolue : on ajoute la différence entre l'oscillation de
 *  cette image et celle de la précédente. Rien n'est donc figé sous la main du
 *  visiteur, et l'arrêt laisse la vue où elle est, sans saut.
 *
 *  La rotation au repos du moteur est suspendue pendant ce temps : deux sources
 *  d'écriture du cap se doubleraient. À l'arrêt, la préférence d'avant revient :
 *  sans préférence, le réglage du document reprend la main.
 *
 *  Phase : la main est à GAUCHE à 0 % de son cycle (`ir-swipe`). Le panorama
 *  glisse avec elle, comme sous un doigt qui tire vers la gauche — le regard,
 *  lui, part à droite : cap + amplitude à l'instant zéro.
 * ==========================================================================*/

export function useOscillationCamera(actif: boolean, amplitudeDeg: number, periodeMs: number) {
  const store = useTourStoreApi();

  useEffect(() => {
    if (!actif) return;
    // L'avis du visiteur (bouton de rotation) est rendu tel quel à l'arrêt.
    const avant = store.getState().context.autoRotate;
    store.getState().setAutoRotatePreference(false);

    const debut = performance.now();
    let ecart = amplitudeDeg;
    let image = 0;
    const pas = (maintenant: number) => {
      const suivant = amplitudeDeg * Math.cos((2 * Math.PI * (maintenant - debut)) / periodeMs);
      const { view, setView } = store.getState();
      setView({ yaw: view.yaw + (suivant - ecart) });
      ecart = suivant;
      image = requestAnimationFrame(pas);
    };
    image = requestAnimationFrame(pas);

    return () => {
      cancelAnimationFrame(image);
      store.getState().setAutoRotatePreference(avant);
    };
  }, [actif, amplitudeDeg, periodeMs, store]);
}
